-- | Provides a widget for selecting an employer and position without any
-- | auto-filling.
module Widgets.Form.EmployerSelect where

import Common

import API.SelEmployer (ContactID, EmployerID, PositionID, SelEmployer, SelPosition, SelContact, contactSelAff, employerSearchSelAff, getEmployer, positionSelAff)
import API.Types (ID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Control.Monad.Except (ExceptT(..), runExceptT)
import Data.Argonaut as J
import Data.Array (catMaybes)
import Data.Array as Arr
import Data.Maybe (maybe)
import Data.String (Pattern(..))
import Data.String as Str
import Error (wrapCritical)
import GraphQL.Join (runGraphQL)
import GraphQL.Shape (QueryField(..))
import GraphQL.Type (dummyId, maybeId)
import Internal.ConcurUtil (loopInfinitely)
import Internal.Util (justIf)
import Record as Rec
import Widgets.BetterSelect (Select, defSelectConfig, emptySearchAff, initSelect', initSimpleSelect', selectWidgetWithId, selectedItem, setSearchAff, setSelected)
import Widgets.EmployerModal (employerCreateModal)
import Widgets.Form.Class (class FormWidget, FormWidgetResult, fieldCanUpdate, fieldClass, fieldId, formWidgetState, setFromJson, setData)
import Widgets.Form.Common (widgetLabelS')


newtype EmployerSelect = EmployerSelect EmployerSelectType
instance showESelect :: Show EmployerSelect where
    show (EmployerSelect s) = "EmployerSelect " <> s.id

type EmployerSelectConfigRow =
    ( id :: String, cls :: String, label :: String )
type EmployerSelectConfig = { contact :: Boolean | EmployerSelectConfigRow }
type EmployerSelectType =
    { employer :: Select SelEmployer
    , position :: Select SelPosition
    , contact :: Maybe (Select SelContact)
    , loadSelection :: Maybe { employer :: ID, position :: ID }
    | EmployerSelectConfigRow
    }

initEmployerSelect :: EmployerSelectConfig -> EmployerSelect
initEmployerSelect { id, cls, label, contact } = EmployerSelect
    { id, cls, label
    , employer: initSelect' (id <> "-employer") (employerSearchSelAff dummyId dummyId) $
        defSelectConfig { placeholder = Just $ lookupTranslation "select-choose-search" }
    , position: initSimpleSelect' (id <> "-position") [] $
        defSelectConfig { placeholder = Just $ lookupTranslation "select-choose-search" }
    , contact: justIf contact $ initSimpleSelect' (id <> "-contact") [] $
        defSelectConfig { placeholder = Just $ lookupTranslation "select-choose-search" }
    , loadSelection: Nothing
    }

instance employerSelectFW :: FormWidget EmployerSelect where
    getId (EmployerSelect state) = state.id
    isLeaf _ = true
    asField (EmployerSelect state) = Just $ QScalar state.id
    fieldPaths fc (EmployerSelect state) = [ fieldId fc state.id ]
    toWidget fc (EmployerSelect state) = renderSelects renderState
      where
        editable = fieldCanUpdate fc state.id
        docId = maybe dummyId _.id fc.doc
        studentId = maybe dummyId _.student.id fc.doc

        setEditable :: forall b. Select b -> Select b
        setEditable sel = sel { config { editable = editable } }

        renderState = state
            { employer = setEditable $ setSearchAff state.employer $ employerSearchSelAff docId studentId
            , position = setEditable state.position
            , contact = map setEditable state.contact
            }

        employerSelID = fieldId fc renderState.employer.id
        positionSelID = fieldId fc renderState.position.id
        contactSelId contact = fieldId fc contact.id

        renderSelects :: EmployerSelectType -> Widget HTML (FormWidgetResult EmployerSelect)
        renderSelects currentState = formWidgetState <$> EmployerSelect <$> D.div
            [ P.className $ fieldClass currentState
            , P.key currentState.id
            ]
            [ widgetLabelS' fc currentState
            , D.div
                [ P.className "search-subform-select-wrapper w-full" ] $ catMaybes
                [ Just $ D.label [P.htmlFor employerSelID]
                    [ D.text $ lookupTranslation "employer" ]
                , Just $ onSelectedEmployer <$>
                    selectWidgetWithId employerSelID currentState.employer
                , justIf (editable && canCreateEmployers) $ D.div [ P.className "employer-edit-buttons" ] $ catMaybes
                    [ Just addBtn
                    , map editBtn $ map _.value $ selectedItem currentState.employer
                    ]
                , Just $ D.label [P.htmlFor positionSelID]
                    [ D.text $ lookupTranslation "position" ]
                , Just $ currentState { position = _ } <$>
                    selectWidgetWithId positionSelID currentState.position
                , (flip map) currentState.contact \contact -> D.label [P.htmlFor $ contactSelId contact]
                    [ D.text $ lookupTranslation "contact" ]
                , (flip map) currentState.contact \contact ->
                    currentState { contact = _ } <$> Just <$>
                        selectWidgetWithId (contactSelId contact) contact
                ]
            ]
          where
            canCreateEmployers = maybe false _.can.create_employers fc.doc

            onSelectedEmployer empSel = currentState
                { employer = empSel
                , position = setEditable $ (flip setSelected) Nothing $
                    setSearchAff currentState.position $
                        maybe emptySearchAff (\e -> positionSelAff docId studentId e.value.id) $
                        selectedItem empSel
                , contact = (flip map) currentState.contact \contact -> setEditable $
                    (flip setSelected) Nothing $
                    setSearchAff contact $
                        maybe emptySearchAff (\e -> contactSelAff docId studentId e.value.id) $
                        selectedItem empSel
                }

            addBtn :: Widget HTML EmployerSelectType
            addBtn = do
                let btn = D.button
                        [ P.className "btn employer-add-btn", P.onClick ]
                        [ D.text $ lookupTranslation $ "add-employer"
                        ]
                _ <- btn
                eid <- employerCreateModal Nothing <|> loopInfinitely btn
                case eid of
                    Just id -> setCreatedEmployer id
                    Nothing -> pure currentState

            editBtn :: SelEmployer -> Widget HTML EmployerSelectType
            editBtn emp = do
                let btn = D.button
                        [ P.className "btn employer-add-btn", P.onClick ]
                        [ D.text $ lookupTranslation "edit-employer" <> truncName emp.company
                        ]
                _ <- btn
                eid <- employerCreateModal (Just emp.id) <|> loopInfinitely btn
                case eid of
                    Just id -> setCreatedEmployer id
                    Nothing -> pure currentState

            truncName :: String -> String
            truncName inp = if Arr.length parts > 2
                then intercalate " " parts <> "..."
                else inp
              where
                parts = Str.split (Pattern " ") inp

            setCreatedEmployer :: EmployerID -> Widget HTML EmployerSelectType
            setCreatedEmployer id = wrapCritical =<< runExceptT do
                emp <- ExceptT $ affAction $ runGraphQL $ getEmployer id
                        (maybeId $ _.id <$> fc.doc)
                        (maybeId $ _.student.id <$> fc.doc)
                let item = { label: emp.company, value: emp }
                pure currentState
                    { employer = setEditable $ setSelected currentState.employer $ Just item
                    , position = setEditable $ setSearchAff currentState.position $ positionSelAff docId studentId emp.id
                    , contact = (flip map) currentState.contact
                        \contact -> setEditable $ setSearchAff contact $ contactSelAff docId studentId emp.id
                    }

    getData _ (EmployerSelect state) =  case state.contact of
        Nothing -> J.encodeJson always
        Just contact -> J.encodeJson $ Rec.merge always
            { contact: toFormCont <$> _.value <$> selectedItem contact }
      where
        -- Always included items
        always =
            { employer: (\e ->
                { id: e.value.id
                , company: e.value.company
                }) <$> selectedItem state.employer
            , position: toFormPos <$> _.value <$> selectedItem state.position
            }

    -- This is a hack to work around the fact that PHP doesn't properly
    -- distinguish between empty arrays and empty objects, even when
    -- serializing to JSON, which does make that distinction.
    setData fc state json | json == J.fromArray [] =
        setData fc state J.jsonEmptyObject

    setData fc outer@(EmployerSelect state) json = flip (setFromJson outer) json (\(d :: BackendEmployerSelData) ->
        Right $ EmployerSelect $ state
        { employer = setSelected state.employer $
            map (\e -> {label: e.company, value: e}) d.employer
        , position =
            (flip setSelected)
                (map (\p -> {label: p.label, value: p}) $ map fromFormPos d.position) $
            setSearchAff state.position $
                maybe emptySearchAff (\e -> positionSelAff docId studentId e.id) $ d.employer
        , contact = (flip map) state.contact \contact ->
            (flip setSelected)
                (map (\p -> {label: p.name, value: p}) $ map fromFormCont d.contact) $
            setSearchAff contact $
                maybe emptySearchAff (\e -> contactSelAff docId studentId e.id) $ d.employer
        })
      where
        docId = maybe dummyId _.id fc.doc
        studentId = maybe dummyId _.student.id fc.doc

type BackendEmployerSelData =
    { employer :: Maybe FormEmployerData
    , position :: Maybe FormPositionData
    , contact :: Maybe FormContactData
    }
-- Because these JSON structures are saved in the form data on the backend, we
-- define them separately here to try and ensure the structure doesn't change.
type FormEmployerData = { id :: EmployerID, company :: String }
type FormPositionData = { id :: PositionID, name :: String }
type FormContactData = { id :: ContactID, name :: String }

fromFormPos :: FormPositionData -> SelPosition
fromFormPos { id, name } = { id, label: name }
toFormPos :: SelPosition -> FormPositionData
toFormPos { id, label } = { id, name: label }

fromFormCont :: FormContactData -> SelContact
fromFormCont { id, name } = { id, name }
toFormCont :: SelContact -> FormContactData
toFormCont { id, name } = { id, name }
